import { yupResolver } from '@hookform/resolvers/yup'
import { LineLinkAlt01 } from '@qr-front/icons/line-link-alt-01'
import { useAtom } from 'jotai'
import { atomWithReset, RESET } from 'jotai/utils'
import { useTranslations } from 'next-intl'
import { forwardRef, useImperativeHandle } from 'react'
import { Resolver, useForm } from 'react-hook-form'
import { maxLength } from 'utils/inputChangeUtils'
import * as yup from 'yup'
import { InferType } from 'yup'

import { transformBackendResponseKey } from '@/config'
import { URL_MAX_LENGTH } from '@/constants'
import { getStore } from '@/store'
import { TTranslationFunction } from '@/types'
import { validateContentForm } from '@/utils'

import { GENERATOR_FORM_ANIMATION_DURATION } from '../components/GeneratorFormSection'
import { RhcField } from '../components/RhcField'
import { urlSchema } from './fieldSchemas'
import { TContentFormHandle, TQrContentFormSection } from './qrContentFormTypes'
import { useSubmitStepOnEnter } from './useSubmitStepOnEnter'
import { useSyncFormSectionValidity } from './useSyncFormSectionValidity'

export const WEBSITE_URL_FORM_ID = 'website-url'

export const getWebsiteUrlSchema = (t: TTranslationFunction) =>
	yup.object({
		websiteUrl: urlSchema(t, { required: true }),
	})

export type TWebsiteUrlFormField = InferType<
	ReturnType<typeof getWebsiteUrlSchema>
>

export const websiteUrlAtom = atomWithReset<string>('')

export const resetWebsiteUrlForm = () => {
	getStore().set(websiteUrlAtom, RESET)
}

export const WebsiteUrlForm = forwardRef<TContentFormHandle>((_, ref) => {
	const t = useTranslations()
	const [websiteUrl, setWebsiteUrl] = useAtom<string>(websiteUrlAtom)
	const submitStepOnEnter = useSubmitStepOnEnter()

	const form = useForm<TWebsiteUrlFormField>({
		defaultValues: {
			websiteUrl: getStore().get(websiteUrlAtom),
		},
		mode: 'onTouched',
		reValidateMode: 'onChange',
		resolver: yupResolver(
			getWebsiteUrlSchema(t),
		) as unknown as Resolver<TWebsiteUrlFormField>,
		shouldUnregister: false,
	})

	const {
		formState: { isValid, errors },
		handleSubmit,
	} = form

	useSyncFormSectionValidity({ formId: WEBSITE_URL_FORM_ID, isValid })

	useImperativeHandle(
		ref,
		() => ({
			trigger: (property: string, message: string) => {
				property = property === 'url' ? 'websiteUrl' : property
				message = t(transformBackendResponseKey(message))
				form.setError(property as keyof TWebsiteUrlFormField, {
					message,
				})
			},
			validate: () =>
				validateContentForm(
					(onValid, onInvalid) =>
						handleSubmit(onValid, async (errors) => {
							onInvalid(errors)
						}),
					GENERATOR_FORM_ANIMATION_DURATION * 1.25,
				),
		}),
		[handleSubmit],
	)

	return (
		// Android Chrome turns Enter into a native jump to the next field on the
		// page (no keydown) unless the input's form has no other fields
		<form noValidate onSubmit={(event) => event.preventDefault()}>
			<RhcField
				type="url"
				form={form}
				path="websiteUrl"
				value={websiteUrl}
				required
				setValue={(value) => setWebsiteUrl(value || '')}
				fieldName="website_url"
				onChangeTransform={(event) =>
					maxLength(URL_MAX_LENGTH + 1)(event)
				}
				{...submitStepOnEnter}
				className="w-full"
				label={t('generator__content_form_section__website__url_label')}
				placeholder={t(
					'generator__content_form_section__website__url_placeholder',
				)}
				tooltip={t(
					'generator__content_form_section__website__url_tooltip',
				)}
				error={errors.websiteUrl?.message}
			/>
		</form>
	)
})

WebsiteUrlForm.displayName = 'WebsiteUrlForm'

export const websiteUrlFormConfig = (
	t: TTranslationFunction,
): TQrContentFormSection => ({
	id: WEBSITE_URL_FORM_ID,
	title: t('generator__content_form_section__website__url_title'),
	description: t('generator__content_form_section__website__url_caption'),
	icon: <LineLinkAlt01 />,
	Content: WebsiteUrlForm,
})
